<script lang="ts">
	import {
		Button,
		Container,
		Modal,
		Text,
		toast,
		Toggle,
		VerificationCode,
	} from '@Shelly/shelly-design-system/index';
	import { capFirstChar } from 'src/helpers/utilities';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import { onDestroy, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { app, os } from 'src/stores/session/native-bridge';
	import {
		control_bluetooth_device_ios,
		control_encrypt_bluetooth_device_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		control_bluetooth_device_android,
		control_encrypt_bluetooth_device_android,
	} from 'src/services/native_container_requests_android';
	import { bulkUpdate } from '../ApplicationSettings/app_settings_helper';
	import { devices_config } from '@devices/index';
	import { get_mode, real_id } from 'src/helpers/interface_helpers';
	import { BLU_SETTINGS_THAT_NEED_STATUS, TEMPERATURE_SENSORS_IN_CELSIUS } from 'src/constants';
	import { setCommandTitle } from 'src/helpers/ble_settings_helpers';
	import { verifySettingUnsupportedFWVersion } from 'src/helpers/firmware_helpers';
	import { BLU_STATUS } from 'src/types/requests/settings/blu_status';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import DynamicAlert from './DynamicAlert.svelte';

	export let device;
	export let usedInSidepanel = false;

	$: device = $devices[real_id(device?.id)] ?? device;

	const reverbBleProcess = () => {
		if (!$app) return;
		controlBleDevice.updateDeviceId(device.id);
		controlBleDevice.updateProcessRunning(true);
		$os === 'IOS'
			? control_bluetooth_device_ios(device.id, 'connect')
			: control_bluetooth_device_android(device.id, 'connect');
	};

	onMount(() => {
		if (
			$controlBleDevice.currentStatusIndex === BLU_STATUS.UNHANDLED_STATE &&
			$controlBleDevice.connectionState === BLU_STATUS.BUTTON_PRESS_REQUIRED
		) {
			reverbBleProcess();
		}
	});

	onDestroy((): void => {
		if (!$app) return;
		// Prevent check if we have been asked to turn on bluetooth
		// but we have declined, we do not send a disconnect command
		if (
			$controlBleDevice.currentStatusIndex === BLU_STATUS.UNHANDLED_STATE &&
			$controlBleDevice.connectionState === BLU_STATUS.BUTTON_PRESS_REQUIRED
		) {
			return;
		}

		if (!usedInSidepanel) {
			if ($controlBleDevice.connectionState === 2) {
				$os === 'IOS'
					? control_bluetooth_device_ios(device.id, 'disconnect')
					: control_bluetooth_device_android(device.id, 'disconnect');
			} else {
				$os === 'IOS'
					? control_bluetooth_device_ios(device.id, 'abortConnecting')
					: control_bluetooth_device_android(device.id, 'disconnect');
			}
			controlBleDevice.reset();
		}
	});

	let showExpiredMsg = false;
	let initialized = false;
	let showModal = false;
	let passCode = '';

	const setCommand = (command, bulk_value, encrypting = false) => {
		if (command === 'encrypt' && !encrypting) {
			showModal = true;
			return;
		}

		if ($controlBleDevice.connectionState === 2) {
			if (command === 'encrypt') {
				$os === 'IOS'
					? control_encrypt_bluetooth_device_ios(device.id, passCode)
					: control_encrypt_bluetooth_device_android(device.id, passCode);
			} else {
				$os === 'IOS'
					? control_bluetooth_device_ios(device.id, command)
					: control_bluetooth_device_android(device.id, command);
			}

			bulkUpdate(device.id, {
				[bulk_value]:
					command === 'encrypt'
						? true
						: command === 'decrypt'
							? false
							: command.startsWith('enable'),
				...(passCode && { key_enc: passCode }),
				...(TEMPERATURE_SENSORS_IN_CELSIUS.includes(device.type) &&
					bulk_value === 'fahrenheitEnabled' && {
						ht_ui: {
							temperature_unit: command.startsWith('enable') ? 'F' : 'C',
						},
					}),
			});

			showModal = false;
			passCode = '';
			const title = setCommandTitle(command, device);
			toast.open({
				type: 'success',
				title,
			});
		} else {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'connect')
				: control_bluetooth_device_android(device.id, 'connect');
		}
	};

	// Handle the different states of the BLE device and initialize or start the process
	$: switch ($controlBleDevice.currentStatusIndex) {
		case null: // no state
			reverbBleProcess();
			break;
		case BLU_STATUS.UNHANDLED_STATE:
		case BLU_STATUS.BUTTON_PRESS_REQUIRED:
		case BLU_STATUS.CONNECT_TO_DEVICE:
			initialized = false;
			break;
		// the device is connected and ready while it offers an OTA update
		case BLU_STATUS.DEVICE_OTA_AVAILABLE:
		case BLU_STATUS.DEVICE_OTA_REQUIRED:
			initialized = true;
			break;
		// on device_processed (2) add the initialized state
		case BLU_STATUS.DEVICE_PROCESSED:
			showExpiredMsg = true;
			initialized = true;
			break;
		// on disconnect from the device start the process again
		case BLU_STATUS.DISCONNECT_FROM_DEVICE:
			reverbBleProcess();
			break;
		default:
			break;
	}

	let settings = readDeviceSettings(device.id);
	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore ?? settings;
	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;
	const mode = get_mode(device, status, settings);

	let checkedState: any = {};
	let requestedState = {};

	$: bluSettings =
		devices_config[device.type].modes[mode]?.bluetooth_settings
			?.map((component) => {
				const bluSetting = BLU_SETTINGS_THAT_NEED_STATUS.find(
					(blu_setting) => blu_setting.setting === component.title,
				);
				if (bluSetting)
					return {
						...bluSetting,
						min_ver: component.min_ver,
						checked: device[bluSetting.bulk_value],
					};
			})
			.filter((setting) => {
				if (!setting || !Object.keys(device)?.includes(setting?.bulk_value)) return false;
				if (
					setting.min_ver &&
					verifySettingUnsupportedFWVersion(device, settings, setting.min_ver, status)
				) {
					return false;
				}
				return true;
			}) || [];

	// Sync checkedState only if:
	// - The user hasn't made a request OR
	// - The latest device state differs from the last requested state
	$: {
		bluSettings.forEach((setting) => {
			// If we haven't made a request or device confirms a different state, update UI
			if (
				requestedState[setting.bulk_value] === undefined ||
				setting.checked === requestedState[setting.bulk_value]
			) {
				checkedState[setting.bulk_value] = setting.checked;
				delete requestedState[setting.bulk_value]; // Reset tracking when the device confirms
			}
		});
	}
</script>

<DynamicAlert {showExpiredMsg} />

{#each bluSettings as setting}
	{#if $showNewLayout}
		{#if setting?.text_key}
			<Text styleType="body-small" class="mb-s">
				{$format(setting.text_key)}
			</Text>
		{/if}
		<Toggle
			aria-label={capFirstChar($format('toggle_blu_setting', { default: 'Toggle setting' }))}
			checked={checkedState[setting.bulk_value]}
			disabled={!initialized || !$app}
			size="m"
			info={setting.info_key ? $format(setting.info_key) : undefined}
			on:click={() => {
				checkedState[setting.bulk_value] = !checkedState[setting.bulk_value];
				requestedState[setting.bulk_value] = checkedState[setting.bulk_value];
				setCommand(
					!checkedState[setting.bulk_value] ? setting.disable_command : setting.enable_command,
					setting.bulk_value,
				);
			}}
			id="toggle-blu-{setting.bulk_value}"
		>
			<span class={checkedState[setting.bulk_value] ? 'text-bold' : ''}>
				{capFirstChar($format(setting.title_key))}
			</span>
		</Toggle>
		<hr class="divider mt-m mb-m" />
	{:else}
		<Container padding="xs" type="modalContainer" class="border-color-transparent mb-s">
			{#if setting?.text_key}
				<Text styleType="body-small" class="mb-s">
					{$format(setting.text_key)}
				</Text>
			{/if}
			<Toggle
				aria-label={capFirstChar($format('toggle_blu_setting', { default: 'Toggle setting' }))}
				checked={checkedState[setting.bulk_value]}
				disabled={!initialized || !$app}
				info={setting.info_key ? $format(setting.info_key) : undefined}
				on:click={() => {
					checkedState[setting.bulk_value] = !checkedState[setting.bulk_value];
					requestedState[setting.bulk_value] = checkedState[setting.bulk_value];
					setCommand(
						!checkedState[setting.bulk_value]
							? setting.disable_command
							: setting.enable_command,
						setting.bulk_value,
					);
				}}
				id="toggle-blu-{setting.bulk_value}"
			>
				<span class={checkedState[setting.bulk_value] ? 'text-bold' : ''}>
					{capFirstChar($format(setting.title_key))}
				</span>
			</Toggle>
		</Container>
	{/if}
{/each}

<Modal
	title={capFirstChar('encrypt device')}
	on:close={() => {
		checkedState.encrypted = false;
		showModal = false;
		passCode = '';
	}}
	{showModal}
>
	<Container slot="content" class="text-center" padding="0">
		<Text class="mb-s"
			>{$format('modal_text_set_encryption_pass', {
				default: 'Set a 6 digit pass code to encrypt your device',
			})}</Text
		>

		<VerificationCode
			id="verificationcode-y10bn5w"
			a11yProps={{
				fieldsAriaLabel: [
					$format('label_verification_code_digit', {
						default: 'Verification code digit 1',
					}),
					$format('label_verification_code_digit', {
						default: 'Verification code digit 2',
					}),
					$format('label_verification_code_digit', {
						default: 'Verification code digit 3',
					}),
					$format('label_verification_code_digit', {
						default: 'Verification code digit 4',
					}),
					$format('label_verification_code_digit', {
						default: 'Verification code digit 5',
					}),
					$format('label_verification_code_digit', {
						default: 'Verification code digit 6',
					}),
				],
			}}
			class="mb-m"
			bind:value={passCode}
			numberOfFields={6}
			on:value_change={(e) => {
				passCode = e.detail.value;
			}}
		/>

		<Button on:click={() => setCommand('encrypt', 'encrypted', true)} disabled={passCode.length < 6}
			>{capFirstChar($format('button_encrypt', { default: 'encrypt' }))}</Button
		>
	</Container>
</Modal>
